import React from 'react';
import { getEnvDomain } from '../../config';
import {
    topTenHoldingsUtil,
    quarterlyTopTenHoldingsUtil,
    EQUITY_ETFS
} from './util';
import { displayShortDateOrNA, setCardID } from '../../utils/Format/Format';
import {
    quarterEndBeforeInception,
    getBoradAssetCategory
} from '../../utils/fund-info';
import { CARD_NAMES } from '../../utils/constants';
import styles from './TopTenHoldings.css';
import Card from '../../components/Card/Card';
import ErrorMessageNewFund from '../../components/ErrorMessages/ErrorMessageNewFund';
import ErrorMessageNoData from '../../components/ErrorMessages/ErrorMessageNoData';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import Table from '../../components/Table/Table';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import Toggle from '../../components/Toggle/Toggle';
import PeriodSelector from '../../components/PeriodSelector/PeriodSelector';

import { ETF_TOP_TEN_HOLDINGS, TOP_TEN_HOLDINGS } from './queries';
import { isExpectingMonthlyData } from '../../utils/time';

class TopTenHoldingsCard extends React.Component {
    constructor(props) {
        super(props);
        const { etf, ticker } = props;

        this.state = {
            selectedPeriod:
                etf && !EQUITY_ETFS.includes(ticker) ? 'quarter' : 'month'
        };
    }

    togglePeriod = (period) => {
        this.setState({
            selectedPeriod: period
        });
    };

    handleChange = (event) => {
        const date = event.target.value;
        this.setState({
            selectedDate: date
        });
    };

    render() {
        const { etf, ticker } = this.props;
        const { selectedPeriod, selectedDate } = this.state;
        const showToggle = !etf || EQUITY_ETFS.includes(ticker);
        const trpDomain = getEnvDomain(window.location.host);
        return (
            <Card id={setCardID(CARD_NAMES.TOP_TEN_HOLDINGS)}>
                <GqlQueryWrapper
                    query={etf ? ETF_TOP_TEN_HOLDINGS : TOP_TEN_HOLDINGS}
                    variables={{ ticker }}
                    dataFunc={
                        showToggle
                            ? topTenHoldingsUtil(selectedPeriod, selectedDate)
                            : quarterlyTopTenHoldingsUtil()
                    }
                    placeholder={() => <TablePreRender />}
                    unbatch
                >
                    {({
                        data: {
                            data: chunkedData,
                            percent10LargestHoldings,
                            asOfDate,
                            inceptionDate,
                            totalHoldings,
                            name,
                            effectiveDates,
                            broadAssetClass,
                            buttonList
                        }
                    }) => {
                        const isNewFund =
                            // etf new fund message if inception is after recent quarter end
                            (etf && quarterEndBeforeInception(inceptionDate)) ||
                            // mf new fund message if fund isn't 1 month old
                            (!etf && !isExpectingMonthlyData(inceptionDate));

                        if (!chunkedData || !chunkedData.length) {
                            if (isNewFund) {
                                return (
                                    <ErrorMessageNewFund
                                        cardName={CARD_NAMES.TOP_TEN_HOLDINGS}
                                    />
                                );
                            }
                            return (
                                <ErrorMessageNoData
                                    cardName={CARD_NAMES.TOP_TEN_HOLDINGS}
                                />
                            );
                        }

                        return (
                            <React.Fragment>
                                <div className="title-group">
                                    <div className="title">
                                        <TitleWithDescription
                                            title={`Top 10 Holdings${
                                                getBoradAssetCategory(
                                                    broadAssetClass
                                                )
                                                    ? ' - Equity Only'
                                                    : ''
                                            }`}
                                        />
                                    </div>
                                    <div className="buttons">
                                        {showToggle && (
                                            <Toggle
                                                data={buttonList}
                                                selected={selectedPeriod}
                                                toggle={this.togglePeriod}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="captions">
                                    <div>
                                        Represents {percent10LargestHoldings} of{' '}
                                        {['TMSRX', 'TMSSX'].includes(ticker)
                                            ? `Fund Exposure`
                                            : `Total Net Assets`}
                                    </div>
                                    <div className="as-of">
                                        <PeriodSelector
                                            selectedPeriod={selectedPeriod}
                                            data={effectiveDates}
                                            selectedDate={asOfDate}
                                            handleChange={this.handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="tables">
                                    {chunkedData.map(({ data, id }) => (
                                        <div key={id} className="column">
                                            <Table data={{ data }} />
                                        </div>
                                    ))}
                                </div>
                                {EQUITY_ETFS.includes(ticker) || (
                                    <div className="disclaimer">
                                        As of {displayShortDateOrNA(asOfDate)},
                                        the {name} had a total of{' '}
                                        {totalHoldings} holdings.
                                    </div>
                                )}
                                {ticker !== 'RPSIX' &&
                                    !etf && (
                                        <div className="disclaimer">
                                            Prior to May 31, 2024, the
                                            information shown does not reflect
                                            any ETFs that the portfolio may have
                                            held.
                                        </div>
                                    )}

                                <div className="disclaimer">
                                    The total of the holdings in the table may
                                    not equal the percentage of total net assets
                                    shown because of rounding.
                                </div>
                                <div className="disclaimer">
                                    {getBoradAssetCategory(broadAssetClass) ||
                                    ['RPSIX', 'PRSGX', 'PSILX'].includes(ticker)
                                        ? `Holdings are as of the date indicated and are subject to change.`
                                        : `Holdings are shown at the issuer/company level as of the date indicated and are subject to change.`}
                                </div>
                                <a
                                    className="view-all"
                                    href={
                                        etf
                                            ? `https://${trpDomain}/personal-investing/funds/etfs/prospectuses-reports/portfolio-holdings.html`
                                            : `https://${trpDomain}/personal-investing/funds/mutual-funds/prospectuses-reports/portfolio-holdings.html`
                                    }
                                >
                                    View the Complete Portfolio of Investments
                                </a>
                            </React.Fragment>
                        );
                    }}
                </GqlQueryWrapper>
                <style jsx>{styles}</style>
            </Card>
        );
    }
}

export default TopTenHoldingsCard;
